// Shared chrome for /preview/* and /studio/* — left cell-tree sidebar +
// right pane that renders whichever editor component the route passes in.
//
// Token mapping (replaces raw hex literals from the old per-route copies):
//   bg-muted             ← --surface-muted        warm-white  (was selected bg)
//   text-ink-deep        ← --brand-ink-deep        near-black  (was selected text)
//   text-foreground      ← --surface-fg            dark        (was leaf text)
//   text-foreground-soft ← --surface-fg-soft       muted       (was folder/loading text)
//   border-border-soft   ← --surface-border-soft   light       (was divider border)
//
// Data-loading strategy:
//   'glob'     — Preview: build-time snapshot via cell-loader (listCells + loadCell).
//   'manifest' — Studio:  live fetch from projector GET /content/:path so
//                in-progress edits are visible on next navigation.
//
// The right-pane component receives `{ cellPath: string }` and is fully
// responsible for its own loading + error states (ReadOnlyEditor or
// TemplateStudio handle this internally).

import type { ComponentType } from 'react';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { listCells, encodeCellPath } from '../lib/cell-loader';

// ─── Tree types + helpers ────────────────────────────────────────────────────

interface TreeNode {
  name: string;
  path?: string;   // present on leaf (file) nodes
  children: TreeNode[];
}

function buildTree(cells: string[]): TreeNode {
  const root: TreeNode = { name: '', children: [] };
  for (const cell of cells) {
    const parts = cell.split('/');
    let node = root;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isLeaf = i === parts.length - 1;
      let child = node.children.find((c) => c.name === part);
      if (!child) {
        child = { name: part, children: [] };
        node.children.push(child);
      }
      if (isLeaf) child.path = cell;
      node = child;
    }
  }
  return root;
}

function containsPath(node: TreeNode, path: string): boolean {
  if (node.path === path) return true;
  return node.children.some((c) => containsPath(c, path));
}

function sortedChildren(node: TreeNode): TreeNode[] {
  // Folders first, then files; alphabetical within each group.
  return [...node.children].sort((a, b) => {
    const aFile = a.path !== undefined;
    const bFile = b.path !== undefined;
    if (aFile !== bFile) return aFile ? 1 : -1;
    return a.name.localeCompare(b.name);
  });
}

// ─── TreeRow ─────────────────────────────────────────────────────────────────

function TreeRow({
  node,
  currentPath,
  level,
  routePrefix,
}: {
  node: TreeNode;
  currentPath: string;
  level: number;
  routePrefix: string;
}) {
  if (node.path) {
    const selected = node.path === currentPath;
    return (
      <Link
        to={`/${routePrefix}/${encodeCellPath(node.path)}`}
        data-testid="cell-link"
        data-cell-path={node.path}
        className={[
          'block font-mono text-xs no-underline',
          'py-0.5',
          selected
            ? 'bg-muted text-ink-deep'
            : 'bg-transparent text-foreground',
        ].join(' ')}
        style={{ paddingLeft: 6 + level * 12, paddingRight: 6 }}
      >
        {node.name}
      </Link>
    );
  }
  const open = containsPath(node, currentPath) || level === 0;
  return (
    <details open={open}>
      <summary
        className="font-mono text-xs text-foreground-soft cursor-pointer py-0.5"
        style={{ paddingLeft: 6 + level * 12, paddingRight: 6 }}
      >
        {node.name}/
      </summary>
      {sortedChildren(node).map((c) => (
        <TreeRow
          key={c.name}
          node={c}
          currentPath={currentPath}
          level={level + 1}
          routePrefix={routePrefix}
        />
      ))}
    </details>
  );
}

// ─── CellTreeShell ────────────────────────────────────────────────────────────

export interface CellTreeShellProps {
  /**
   * The component to render in the right pane. Receives `{ cellPath }` where
   * cellPath is the content-relative path decoded from the URL param (e.g.
   * "subscribe/sign.ready.md"). The component is responsible for its own
   * loading + error display.
   */
  rightPane: ComponentType<{ cellPath: string }>;
  /**
   * Route prefix for tree link hrefs. 'preview' → /preview/:path,
   * 'studio' → /studio/:path.
   */
  routePrefix: 'preview' | 'studio';
}

export function CellTreeShell({ rightPane: RightPane, routePrefix }: CellTreeShellProps) {
  const params = useParams<{ '*': string }>();
  const cellPath = params['*'] ?? '';
  const cells = useMemo(() => listCells(), []);
  const tree = useMemo(() => buildTree(cells), [cells]);

  return (
    <div className="flex" style={{ height: 'calc(100vh - 64px)' }}>
      <aside
        className="w-80 overflow-y-auto border-r border-border-soft shrink-0 p-3"
      >
        <h2 className="text-sm font-medium mt-0 mb-2 text-foreground">Cells</h2>
        {sortedChildren(tree).map((c) => (
          <TreeRow
            key={c.name}
            node={c}
            currentPath={cellPath}
            level={0}
            routePrefix={routePrefix}
          />
        ))}
      </aside>
      <main className="flex-1 overflow-y-auto p-4">
        <RightPane cellPath={cellPath} />
      </main>
    </div>
  );
}
